import StaticPageLayout, { ContentBlock } from 'components/StaticPageLayout'
import Tag from 'components/Tag'
import ComponentFAQ from 'components/directus/FAQ'
import {
  gridBaseStyle,
  imageBaseStyle as imageBaseStyleDesktop,
  otherFeaturesBaseStyle,
} from 'components/features-page/DesktopFeaturesPage'
import ExtensionHowToSetUp from 'components/features-page/ExtensionHowToSetUp'
import FeatureBox from 'components/features-page/FeatureBox'
import FeatureImage from 'components/features-page/FeatureImage'
import FeaturePageDownloadButtons from 'components/features-page/FeaturePageDownloadButtons'
import FeaturePageHero from 'components/features-page/FeaturePageHero'
import FeatureSocialProof from 'components/features-page/FeatureSocialProof'
import FeatureText from 'components/features-page/FeatureText'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import adCrusherIcon from 'images/ad-crusher.svg'
import advancedModeIcon from 'images/advanced-mode.svg'
import autoConnectIcon from 'images/auto-pilot.svg'
import blockerIcon from 'images/blocker.svg'
import checkIcon from 'images/check-icon.svg'
import connectivity from 'images/connectivity.svg'
import cookieGoIcon from 'images/cookie-go-away.svg'
import cookieIcon from 'images/cookie-monster.svg'
import dataEncryptedIcon from 'images/data-encrypted-icon.svg'
import doNotDisturbIcon from 'images/do-not-disturb.svg'
import failoverIcon from 'images/failover.svg'
import smokewallIcon from 'images/firewall.svg'
import globeIcon from 'images/globe-icon.svg'
import languageWarpIcon from 'images/language-warp.svg'
import locationWarpIcon from 'images/location-warp.svg'
import malwareEvaderIcon from 'images/malware-evader.svg'
import privacyIcon from 'images/privacy-dark.svg'
import proxyPortIcon from 'images/proxy-port.svg'
import socialDistancingIcon from 'images/social-distancing.svg'
import splitPersonalityIcon from 'images/split-personality.svg'
import timeWarpIcon from 'images/time-warp.svg'
import trackerEradicatorIcon from 'images/tracker-eradicator.svg'
import unlockContentIcon from 'images/unlock-content-icon.svg'
import webrtcIcon from 'images/webrtc-slayer.svg'
import workersBlockIcon from 'images/worker-block.svg'
import { Flex, Grid, Text } from 'theme-ui'
import useBreakpointIndex from 'theme/useBreakpointIndex'
import getEnglishStrings from 'utils/getEnglishStrings'
import EmbeddedVideo from './EmbeddedVideo'

const imageBaseStyle = {
  ...imageBaseStyleDesktop,
  maxWidth: '32rem',
}

export default function BrowserFeaturesPage({ path }: { path: string }): JSX.Element {
  const data = useStaticQuery<Queries.ExtensionFeaturesPageQuery>(graphql`
    query ExtensionFeaturesPage {
      directus {
        general_translations {
          strings {
            features
            frequently_asked_questions
            languages_code {
              code
            }
          }
        }
        browser_page {
          strings {
            ad_crusher
            ad_crusher_subtitle
            advanced_blocking
            advanced_blocking_subtitle
            advanced_blocking_subtitle_markdown
            advanced_blocking_title
            advanced_mode
            advanced_privacy
            advanced_mode_subtitle
            advanced_privacy_first_subtitle
            advanced_privacy_second_subtitle
            advanced_privacy_title
            cookie_go
            cookie_go_subtitle
            cookie_monster
            cookie_monster_subtitle
            custom_whitelisting
            do_not_disturb
            do_not_disturb_subtitle
            download_for_chrome
            download_for_edge
            failover
            download_for_firefox
            failover_subtitle
            language_warp
            language_warp_subtitle
            location_warp
            languages_code {
              code
            }
            location_warp_subtitle
            malware_evader
            other_features
            malware_evader_subtitle
            proxy_port
            smokewall
            proxy_port_subtitle
            servers_in_over
            smokewall_subtitle
            social_distancing
            social_distancing_subtitle
            split_personality
            split_personality_subtitle
            strict_no_logging
            subtitle
            time_warp
            time_warp_subtitle
            title
            tracker_eradicator
            tracker_eradicator_subtitle
            unlock_content
            unparalleled_connectivity
            unparalleled_connectivity_subtitle
            unparalleled_connectivity_title
            webrtc_subtitle
            webrtc
            workers_block
            workers_block_subtitle
            meta_description
            meta_keywords
            chrome_title
            chrome_subtitle
            firefox_title
            firefox_subtitle
            edge_title
            edge_subtitle
            chrome_video_title
            firefox_video_title
            chrome_video_url
            firefox_video_url
            auto_connect
            auto_connect_subtitle
          }
          chrome_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          firefox_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          edge_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          social_proof {
            id
            categories(sort: "sort") {
              social_proof_categories_id {
                id
                strings {
                  languages_code {
                    code
                  }
                  name
                }
                items(sort: "sort") {
                  social_proof_items_id {
                    id
                    url
                    score
                    strings {
                      author_description
                      author_name
                      content
                      languages_code {
                        code
                      }
                    }
                    award_badge {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(height: 30, width: 88)
                        }
                      }
                    }
                    author_picture {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(height: 50, width: 50)
                        }
                      }
                    }
                    organization_picture {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(width: 50, height: 50)
                        }
                      }
                    }
                  }
                }
              }
            }
            strings {
              title
              languages_code {
                code
              }
            }
          }
        }
        chrome_app_page {
          strings {
            languages_code {
              code
            }
            meta_description
            meta_keywords
            title
            windscribe_offers
          }
        }
        firefox_app_page {
          strings {
            languages_code {
              code
            }
            meta_description
            meta_keywords
            title
            windscribe_offers
          }
        }
        edge_app_page {
          strings {
            languages_code {
              code
            }
            meta_description
            meta_keywords
            title
            windscribe_offers
          }
        }
      }
    }
  `)

  const browserFeaturesData = getEnglishStrings(data.directus.browser_page?.strings)
  const generalTranslations = getEnglishStrings(data.directus.general_translations?.strings)
  const breakPointIndex = useBreakpointIndex()
  const isMobile = breakPointIndex === 0

  const osType = (() => {
    switch (path) {
      case '/features/chrome/':
        return 'chrome'
      case '/features/firefox/':
        return 'firefox'
      case '/features/edge/':
        return 'edge'
      default:
        return 'chrome'
    }
  })()

  const metaData = {
    firefox: getEnglishStrings(data?.directus?.firefox_app_page?.strings),
    chrome: getEnglishStrings(data?.directus?.chrome_app_page?.strings),
    edge: getEnglishStrings(data?.directus?.edge_app_page?.strings),
  }[osType]

  return (
    <StaticPageLayout
      title={`${metaData?.title} | Windscribe`}
      metaDescription={metaData?.meta_description}
      metaKeywords={metaData?.meta_keywords}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: ['hidden', 'hidden', 'visible'],
          width: '100vw',
          mb: ['4.8rem', '8rem'],
        }}
      >
        <FeaturePageHero path={path} />

        <ContentBlock sxx={{ gap: ['4.8rem', '4.8rem', '8rem'] }}>
          <ExtensionHowToSetUp sxx={{ mt: '2.4rem' }} path={path} />
          {osType !== 'edge' && (
            <EmbeddedVideo
              videoSrc={
                (osType === 'chrome'
                  ? browserFeaturesData?.chrome_video_url
                  : browserFeaturesData?.firefox_video_url) ?? ''
              }
              sxx={{ maxWidth: 'maxContentWidth' }}
            />
          )}
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <Flex sx={{ flexDirection: 'column', gap: '4.8rem', alignItems: 'center' }}>
              <Tag title={generalTranslations?.features ?? ''} />
              <Text
                variant={isMobile ? 'h2Small' : 'h2Big'}
                sx={{ textAlign: 'center', margin: '0 auto', maxWidth: ['none', 'none', '60rem'] }}
              >
                {metaData?.windscribe_offers ?? ''}
              </Text>
              <FeatureImage
                imageSide="left"
                title={browserFeaturesData?.advanced_privacy_title ?? ''}
                category={browserFeaturesData?.advanced_privacy ?? ''}
                firstSubtitle={browserFeaturesData?.advanced_privacy_first_subtitle ?? ''}
                secondSubtitle={browserFeaturesData?.advanced_privacy_second_subtitle ?? ''}
                icon={privacyIcon}
                categoryColour="periwinkle"
                imageStyles={imageBaseStyle}
                sxx={{
                  background:
                    theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.periwinkle25}, ${theme.colors?.transparent}) border-box`,
                  border: '0.2rem solid transparent',
                  borderRadius: '4.8rem 4.8rem 0 0',
                }}
              >
                <StaticImage src="../../images/privacy-app.png" alt="" />
              </FeatureImage>
            </Flex>
            <Grid
              columns={[1, 2]}
              sx={{
                ...gridBaseStyle,
                px: 0,
              }}
            >
              <FeatureBox
                title={browserFeaturesData?.cookie_monster ?? ''}
                subtitle={browserFeaturesData?.cookie_monster_subtitle ?? ''}
                colour="periwinkle"
                icon={cookieIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.do_not_disturb ?? ''}
                subtitle={browserFeaturesData?.do_not_disturb_subtitle ?? ''}
                colour="periwinkle"
                icon={doNotDisturbIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.webrtc ?? ''}
                subtitle={browserFeaturesData?.webrtc_subtitle ?? ''}
                colour="periwinkle"
                icon={webrtcIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.location_warp ?? ''}
                subtitle={browserFeaturesData?.location_warp_subtitle ?? ''}
                colour="periwinkle"
                icon={locationWarpIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.time_warp ?? ''}
                subtitle={browserFeaturesData?.time_warp_subtitle ?? ''}
                colour="periwinkle"
                icon={timeWarpIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.language_warp ?? ''}
                subtitle={browserFeaturesData?.language_warp_subtitle ?? ''}
                colour="periwinkle"
                icon={languageWarpIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.split_personality ?? ''}
                subtitle={browserFeaturesData?.split_personality_subtitle ?? ''}
                colour="periwinkle"
                icon={splitPersonalityIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.workers_block ?? ''}
                subtitle={browserFeaturesData?.workers_block_subtitle ?? ''}
                colour="periwinkle"
                icon={workersBlockIcon}
              />
            </Grid>
          </Flex>
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <FeatureImage
              imageSide="right"
              title={browserFeaturesData?.advanced_blocking_title ?? ''}
              category={browserFeaturesData?.advanced_blocking ?? ''}
              firstSubtitle={browserFeaturesData?.advanced_blocking_subtitle_markdown ?? ''}
              icon={blockerIcon}
              categoryColour="neonGreen"
              imageStyles={imageBaseStyle}
              sxx={{
                padding: ['2.4rem', '2.4rem', 0],
              }}
            >
              <StaticImage src="../../images/blocker-app.png" alt="" />
            </FeatureImage>
            <Grid
              columns={[1, 2]}
              sx={{
                ...gridBaseStyle,
                px: 0,
              }}
            >
              <FeatureBox
                title={browserFeaturesData?.ad_crusher ?? ''}
                subtitle={browserFeaturesData?.ad_crusher_subtitle ?? ''}
                colour="neonGreen"
                icon={adCrusherIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.tracker_eradicator ?? ''}
                subtitle={browserFeaturesData?.tracker_eradicator_subtitle ?? ''}
                colour="neonGreen"
                icon={trackerEradicatorIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.malware_evader ?? ''}
                subtitle={browserFeaturesData?.malware_evader_subtitle ?? ''}
                colour="neonGreen"
                icon={malwareEvaderIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.social_distancing ?? ''}
                subtitle={browserFeaturesData?.social_distancing_subtitle ?? ''}
                colour="neonGreen"
                icon={socialDistancingIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.cookie_go ?? ''}
                subtitle={browserFeaturesData?.cookie_go_subtitle ?? ''}
                colour="neonGreen"
                icon={cookieGoIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.advanced_mode ?? ''}
                subtitle={browserFeaturesData?.advanced_mode_subtitle ?? ''}
                colour="neonGreen"
                icon={advancedModeIcon}
              />
            </Grid>
          </Flex>
          <Flex sx={{ flexDirection: 'column' }}>
            <FeatureImage
              imageSide="left"
              title={browserFeaturesData?.unparalleled_connectivity_title ?? ''}
              category={browserFeaturesData?.unparalleled_connectivity ?? ''}
              firstSubtitle={browserFeaturesData?.unparalleled_connectivity_subtitle ?? ''}
              icon={connectivity}
              categoryColour="windscribeBlue"
              imageStyles={imageBaseStyle}
              sxx={{
                background:
                  theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.windscribeBlue25}, ${theme.colors?.transparent}) border-box`,
                border: '0.2rem solid transparent',
                borderRadius: '4.8rem 4.8rem 0 0',
              }}
            >
              <StaticImage src="../../images/smokewall-app.png" alt="" />
            </FeatureImage>
            <Grid
              columns={[1, 2]}
              sx={{
                ...gridBaseStyle,
                px: 0,
                width: '100%',
              }}
            >
              <FeatureBox
                title={browserFeaturesData?.auto_connect ?? ''}
                subtitle={browserFeaturesData?.auto_connect_subtitle ?? ''}
                icon={autoConnectIcon}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={browserFeaturesData?.smokewall ?? ''}
                subtitle={browserFeaturesData?.smokewall_subtitle ?? ''}
                colour="windscribeBlue"
                icon={smokewallIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.failover ?? ''}
                subtitle={browserFeaturesData?.failover_subtitle ?? ''}
                colour="windscribeBlue"
                icon={failoverIcon}
              />
              <FeatureBox
                title={browserFeaturesData?.proxy_port ?? ''}
                subtitle={browserFeaturesData?.proxy_port_subtitle ?? ''}
                colour="windscribeBlue"
                icon={proxyPortIcon}
              />
            </Grid>
          </Flex>
          <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: '4rem' }}>
            <Tag title={browserFeaturesData?.other_features ?? ''} />
            <Grid columns={[1, 2, 2]} sx={{ ...otherFeaturesBaseStyle, maxWidth: '60rem' }}>
              <FeatureText
                icon={unlockContentIcon}
                text={browserFeaturesData?.unlock_content ?? ''}
              />
              <FeatureText
                icon={dataEncryptedIcon}
                text={browserFeaturesData?.strict_no_logging ?? ''}
              />
              <FeatureText icon={checkIcon} text={browserFeaturesData?.custom_whitelisting ?? ''} />
              <FeatureText icon={globeIcon} text={browserFeaturesData?.servers_in_over ?? ''} />
            </Grid>
          </Flex>

          <FeaturePageDownloadButtons path={path} hideSignUp />
          <FeatureSocialProof
            data={
              data.directus?.browser_page
                ?.social_proof as Queries.DirectusData_component_social_proof
            }
          />
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: theme => `calc(${theme.sizes?.['maxContentWidth']} + 0.8rem)`,
              width: '100%',
            }}
          >
            <ComponentFAQ
              data={
                data.directus?.browser_page?.[osType + '_faq'] as Queries.DirectusData_component_faq
              }
            />
          </Flex>
        </ContentBlock>
      </Flex>
    </StaticPageLayout>
  )
}
